import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Check, ChevronsUpDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  FormHeader,
  FormInput,
  PrimarySecondaryButtonContainer,
  SubmitButton,
} from '../components/FormComponents';
import { FormErrorAlert } from '@/components/FormErrorAlert';
import { Button } from '@/components/ui/button';
import { Command, CommandGroup, CommandItem, CommandList } from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { advisorFields, advisorFieldsMap, isValidEmailFormat } from '@shared';
import { selectAdvisorAdminPortalState } from '@/model/selectors';
import { postWizardAdvisorFieldUpdateByAdvisorIdRequest } from '@/model/advisorAdminPortalModel';
import { FeatureFlagsEditor } from './FeatureFlagsEditor';

const isFieldValueABooleanString = (fieldValue: string) => {
  return fieldValue === 'true' || fieldValue === 'false';
};

const schema = z
  .object({
    advisorId: z.string().min(1, 'Advisor ID is required'),
    advisorFieldKey: z.enum([
      'organizationName',
      'organizationDisplayName',
      'advisorEmail',
      'isAccountVerified',
      'featureFlags',
    ]),
    advisorFieldValue: z.string().min(1, 'Field value is required'),
  })
  .superRefine((data, ctx) => {
    if (
      advisorFieldsMap[data.advisorFieldKey].type === 'boolean' &&
      !isFieldValueABooleanString(data.advisorFieldValue)
    ) {
      ctx.addIssue({
        path: ['advisorFieldValue'],
        code: z.ZodIssueCode.custom,
        message: `Field value must be either "true" or "false" for ${data.advisorFieldKey}`,
      });
    } else if (data.advisorFieldKey === 'advisorEmail') {
      if (!isValidEmailFormat(data.advisorFieldValue)) {
        ctx.addIssue({
          path: ['advisorFieldValue'],
          code: z.ZodIssueCode.custom,
          message: `Invalid email provided!`,
        });
      }
    } else if (data.advisorFieldKey === 'featureFlags') {
      try {
        JSON.parse(data.advisorFieldValue);
      } catch {
        ctx.addIssue({
          path: ['advisorFieldValue'],
          code: z.ZodIssueCode.custom,
          message: `Invalid JSON format for feature flags`,
        });
      }
    }
  });

export function AdminAdvisorForm() {
  const [open, setOpen] = useState(false);
  const [emailConfirmationWarning, setemailConfirmationWarning] = useState<string | null>(null);
  const hasAcknowledgedRef = useRef<boolean>(false);
  const dispatch = useDispatch();
  const {
    register,
    handleSubmit,
    getValues,
    setValue,
    clearErrors,
    watch,
    formState: { errors },
  } = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
  });

  const selectedFieldKey = watch('advisorFieldKey');

  const { status, errorMessage } = useSelector(selectAdvisorAdminPortalState);

  const onSubmit = (data: z.infer<typeof schema>) => {
    if (
      advisorFieldsMap[data.advisorFieldKey].shouldSendEmailConfirmation &&
      !hasAcknowledgedRef.current &&
      data.advisorFieldValue === 'true'
    ) {
      setemailConfirmationWarning(
        `Updating this value might send an email to the advisor. Please resubmit to confirm.`,
      );
      hasAcknowledgedRef.current = true;
      return;
    }

    setemailConfirmationWarning(null);
    hasAcknowledgedRef.current = false;
    dispatch(postWizardAdvisorFieldUpdateByAdvisorIdRequest(data));
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="space-y-4 text-gray-700"
    >
      <div className="mb-4 space-y-2">
        <FormHeader
          title="Manage Advisor"
          className="mb-0"
        />
        <p className=" text-gray-700">Update fields with the Advisors table.</p>
      </div>
      <FormInput
        label="Advisor ID"
        id="advisorId"
        type="text"
        placeholder="Enter advisor ID"
        error={errors.advisorId}
        register={register('advisorId')}
        required={true}
      />

      <div className="space-y-2">
        <label className="text-sm font-medium">Field to Update</label>
        <Popover
          open={open}
          onOpenChange={setOpen}
        >
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              role="combobox"
              aria-expanded={open}
              className={cn(
                'w-full justify-between text-sm text-gray-700',
                errors.advisorFieldKey && 'border-red-500',
              )}
            >
              {getValues('advisorFieldKey')
                ? advisorFieldsMap[getValues('advisorFieldKey')].label
                : 'Select field...'}
              <ChevronsUpDown className="opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-full p-0">
            <Command>
              <CommandList>
                <CommandGroup>
                  {advisorFields.map(field => (
                    <CommandItem
                      key={field.value}
                      value={field.value}
                      onSelect={currentValue => {
                        setValue('advisorFieldKey', currentValue as any);
                        clearErrors('advisorFieldKey');
                        setOpen(false);
                      }}
                    >
                      {field.label}
                      <Check
                        className={cn(
                          'ml-auto',
                          getValues('advisorFieldKey') === field.value
                            ? 'opacity-100'
                            : 'opacity-0',
                        )}
                      />
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
        {errors.advisorFieldKey && (
          <p className="text-sm text-red-700">Please select a field to update</p>
        )}
      </div>

      {selectedFieldKey === 'featureFlags' ? (
        <div className="space-y-2">
          <label className="text-sm font-medium">Feature Flags</label>
          <FeatureFlagsEditor
            value={getValues('advisorFieldValue') || '{}'}
            onChange={value => {
              setValue('advisorFieldValue', value);
              clearErrors('advisorFieldValue');
            }}
            error={!!errors.advisorFieldValue}
          />
          {errors.advisorFieldValue && (
            <p className="text-sm text-red-700">{errors.advisorFieldValue.message}</p>
          )}
        </div>
      ) : (
        <FormInput
          label="New Value"
          id="advisorFieldValue"
          type="text"
          placeholder="Enter new value"
          error={errors.advisorFieldValue}
          register={register('advisorFieldValue')}
          required={true}
        />
      )}

      <PrimarySecondaryButtonContainer>
        <SubmitButton
          label="Update Field"
          className="translate-y-4"
          isLoading={status === 'loading'}
        />
      </PrimarySecondaryButtonContainer>

      <FormErrorAlert
        mainErrorMessage={emailConfirmationWarning ?? errorMessage ?? ''}
        isWarning={!!emailConfirmationWarning}
        className="translate-y-4"
      />
    </form>
  );
}
